"use client";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { FaTelegram } from "react-icons/fa";

interface ErrorAlertProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  description?: string;
  errorMessage?: string;
  cancelText?: string;
  actionText?: string;
  showSupportLink?: boolean;
  supportInfo?: string;
  onAction?: () => void;
  onCancel?: () => void;
}

export function ErrorAlert({
  isOpen,
  onOpenChange,
  title = "Error Occurred",
  description = "We encountered an issue while processing your request.",
  errorMessage,
  cancelText = "Cancel",
  actionText = "Try Again",
  showSupportLink = true,
  supportInfo,
  onAction,
  onCancel,
}: ErrorAlertProps) {
  
  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
    onOpenChange(false);
  };

  const handleAction = () => {
    if (onAction) {
      onAction();
    }
    onOpenChange(false);
  };

  const openTelegramSupport = () => {
    window.open('https://t.me/batchfinderdev', '_blank');
  };

  // Parse supportInfo to separate account ID from instructions
  const [accountId, supportInstructions] = supportInfo 
    ? supportInfo.split('\n') 
    : ['', ''];

  return (
    <AlertDialog open={isOpen} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-md rounded-xl">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            {title}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-sm text-muted-foreground">
            {description}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {errorMessage && (
          <div className="my-4 p-3 bg-destructive/10 text-destructive rounded-md text-sm font-medium">
            {errorMessage}
          </div>
        )}

        {showSupportLink && (
          <div className="my-4">
            <p className="text-sm mb-3">If the problem persists, please contact our support team:</p>
            
            {accountId && (
              <div className="mb-3 p-2 bg-slate-100 rounded-md border border-slate-200">
                <p className="text-sm font-medium text-foreground">{accountId}</p>
              </div>
            )}
            
            <Button 
              variant="outline" 
              className="w-full flex items-center justify-center gap-2 text-sm" 
              size="sm"
              onClick={openTelegramSupport}
            >
              <FaTelegram className="h-4 w-4 text-[#229ED9]" />
              Contact Support on Telegram
            </Button>
            
            {supportInstructions && (
              <p className="mt-2 text-xs text-muted-foreground">
                {supportInstructions}
              </p>
            )}
          </div>
        )}
        
        <AlertDialogFooter className="mt-4">
          {cancelText && (
            <AlertDialogCancel 
              onClick={handleCancel}
              className="mt-3"
            >
              {cancelText}
            </AlertDialogCancel>
          )}
          {actionText && (
            <AlertDialogAction 
              onClick={handleAction}
              className="bg-destructive hover:bg-destructive/90"
            >
              {actionText}
            </AlertDialogAction>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
} 